import { useTranslation } from 'react-i18next';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronDown, ChevronRight, Clock, Ship, ArrowDown, MapPin, Plane } from '../ui/Icon';
import { Icon } from '../ui/Icon';
import Spinner from '../ui/Spinner';
import SortableStop from './SortableStop';
import DayRouteActions from './DayRouteActions';
import { LEG_MODES } from '../../utils/mapConstants';
import { formatLegDuration, transferKindOf } from '../../utils/legFormatting';
import type { TransferKind, TravelMode } from '../../api/directions.api';
import type { DayRouteGroup } from '../../utils/routeGroups';
import type { ClientSegment } from '../../utils/legFormatting';

interface Props {
  group: DayRouteGroup;
  destination: string;
  collapsed: boolean;
  busy: boolean;
  optimizing: boolean;
  legUnavailable: Record<string, string[]>;
  selectedActivityId: string | null;
  /** Optional stops hidden by the filter. They stay in `group`, unrendered. */
  hiddenStops: Set<string>;
  onToggleCollapsed: () => void;
  onSelectStop: (activityId: string) => void;
  /** Absent = read-only: no dragging, no optimiser, the leg mode only shown. */
  edit?: {
    onSetLegMode: (legIdx: number, mode: TravelMode) => void;
    onOptimize: (hint?: string) => void;
  };
}

/** One day inside the unified panel: its own header, stops, legs and actions. */
export default function DayRouteSection({
  group, destination, collapsed, busy, optimizing, legUnavailable, selectedActivityId,
  hiddenStops, onToggleCollapsed, onSelectStop, edit,
}: Props) {
  const { t } = useTranslation();
  const shownStops = group.stops.filter(s => !hiddenStops.has(s.activity.id));

  return (
    <section className="border-b border-slate-100 dark:border-slate-800 last:border-b-0">
      <div className="sticky top-0 z-10 flex items-center gap-2 px-3 py-2 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-slate-100 dark:border-slate-800">
        <button
          onClick={onToggleCollapsed}
          className="flex items-center gap-2 min-w-0 flex-1 text-left cursor-pointer"
          aria-expanded={!collapsed}
        >
          <Icon as={collapsed ? ChevronRight : ChevronDown} size={14} className="text-slate-400 shrink-0" />
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: group.color }} />
          <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest whitespace-nowrap">
            {t('map.dayLabel', { n: group.day.dayNumber })}
          </span>
          {group.day.title && (
            <span className="text-xs text-slate-400 dark:text-slate-500 truncate">{group.day.title}</span>
          )}
          <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 dark:text-slate-500 shrink-0">
            <Icon as={MapPin} size={10} />
            {shownStops.length}
          </span>
          {busy && <Spinner size="xs" />}
        </button>

        <DayRouteActions
          group={group}
          destination={destination}
          optimizing={optimizing}
          onOptimize={edit?.onOptimize}
        />
      </div>

      {/* Stops but no legs after the trip pass (ADR-0025): the route could not
          be calculated. Saying so beats an empty gap with no explanation. */}
      {!collapsed && !busy && group.stops.length > 1 && group.legs.every(leg => !leg) && (
        <p className="px-3 py-1.5 text-[11px] text-slate-400 dark:text-slate-500">
          {t('map.dayNotCalculated')}
        </p>
      )}

      {/* The drag context is the panel's, not this day's — a stop has to be able
          to land on another day, and a context per section made that impossible. */}
      {!collapsed && (
          <SortableContext items={shownStops.map(s => s.activity.id)} strategy={verticalListSortingStrategy}>
            {/* Iterated over the full list on purpose: `idx` addresses
                `group.legs` and `onSetLegMode`, so filtering the array first
                would point every leg at the wrong pair. */}
            {group.stops.map((stop, idx) => {
              if (hiddenStops.has(stop.activity.id)) return null;
              const nextStop = group.stops[idx + 1];
              // The leg into a hidden stop goes with it: we never asked for the
              // route that skips it, so there is no time to put here.
              const nextHidden = !!nextStop && hiddenStops.has(nextStop.activity.id);
              // The duration sits between two stops, so on its own it reads as
              // "time at" rather than "time from A to B" — this label says which.
              const legLabel = nextStop
                ? t('map.legFromTo', {
                    from: stop.activity.venueName ?? stop.activity.title,
                    to: nextStop.activity.venueName ?? nextStop.activity.title,
                  })
                : undefined;
              const leg = group.legs[idx];
              const transfer = transferKindOf(leg);
              const unavailable = legUnavailable[`${group.day.id}:${idx}`] ?? [];
              return (
                <div key={stop.activity.id}>
                  <SortableStop
                    activity={stop.activity}
                    index={stop.num - 1}
                    color={group.color}
                    hasDetails={!!stop.venue}
                    selected={selectedActivityId === stop.activity.id}
                    draggable={!!edit}
                    onSelect={() => onSelectStop(stop.activity.id)}
                  />
                  {nextStop && !nextHidden && (
                    <div className="flex items-center gap-1.5 pl-8 pr-3 py-1">
                      <span role="img" title={legLabel} aria-label={legLabel} className="shrink-0 ml-1 text-slate-300 dark:text-slate-600">
                        <Icon as={ArrowDown} size={14} />
                      </span>
                      {edit ? (
                        <div className="flex gap-0.5 ml-1">
                          {LEG_MODES.map(({ mode, Icon: LegIcon }) => {
                            const active = !transfer && leg?.mode === mode;
                            const isUnavailable = unavailable.includes(mode);
                            return (
                              <button
                                key={mode}
                                disabled={isUnavailable || busy}
                                title={isUnavailable ? t('map.travelMode.unavailable') : t(`map.travelMode.${mode}` as Parameters<typeof t>[0])}
                                onClick={() => edit.onSetLegMode(idx, mode as TravelMode)}
                                // Un modo no disponible se distingue por perder la
                                // píldora, no por atenuarse: bajarle el gris lo dejaba
                                // por debajo de lo legible y parecía un hueco.
                                className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${
                                  isUnavailable
                                    ? 'text-slate-400 dark:text-slate-500 cursor-not-allowed'
                                    : active
                                      ? 'bg-teal-600 text-white cursor-pointer'
                                      : 'bg-slate-100 text-slate-400 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:hover:bg-slate-700 cursor-pointer'
                                }`}
                              >
                                <Icon as={LegIcon} size={12} />
                              </button>
                            );
                          })}
                        </div>
                      ) : leg && !transfer && (
                        <LegModeBadge mode={leg.mode} />
                      )}
                      {leg && transfer && <TransferBadge kind={transfer} label={leg.segments[0]?.instructions ?? null} />}
                      {leg && !transfer && (
                        <div title={legLabel} className="flex items-center gap-1 ml-1 min-w-0">
                          <Icon as={Clock} size={12} className="text-slate-400 shrink-0" />
                          <span className="text-xs text-slate-400 shrink-0">{formatLegDuration(leg.durationSec)}</span>
                          <FerryBreakdown segments={leg.segments} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </SortableContext>
      )}
    </section>
  );
}

/**
 * A leg that crosses water arrives split into drive · ferry · continue. Show the
 * crossing on its own: "1 h 45 by ferry" reads very differently from a leg that
 * is simply long.
 */
function FerryBreakdown({ segments }: { segments: ClientSegment[] }) {
  const { t } = useTranslation();
  const ferry = segments.find(s => s.mode === 'FERRY');
  if (!ferry) return null;
  return (
    <span
      title={ferry.instructions ?? t('map.travelMode.FERRY')}
      className="flex items-center gap-1 min-w-0 px-1.5 py-0.5 rounded-full bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-900/40"
    >
      <Icon as={Ship} size={11} className="text-sky-600 dark:text-sky-400 shrink-0" />
      <span className="text-[11px] font-medium text-sky-700 dark:text-sky-400 truncate">
        {t('map.travelMode.ferryLeg', { duration: formatLegDuration(ferry.durationSec) })}
      </span>
    </span>
  );
}

/**
 * ADR-0031: a flight or transfer between destinations has no route and no time
 * to show — only what it is. Picking a mode beside it asks for the route.
 */
function TransferBadge({ kind, label }: { kind: TransferKind; label: string | null }) {
  const { t } = useTranslation();
  const text = label ?? t(`map.travelMode.${kind}`);
  return (
    <span title={text} className="flex items-center gap-1 ml-1 min-w-0 px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">
      <Icon as={kind === 'FLIGHT' ? Plane : ArrowDown} size={11} className="text-slate-500 shrink-0" />
      <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300 truncate">{text}</span>
    </span>
  );
}

/** The mode a leg was routed with, for someone who can't change it. */
function LegModeBadge({ mode }: { mode: string }) {
  const { t } = useTranslation();
  const legMode = LEG_MODES.find(m => m.mode === mode);
  if (!legMode) return null;
  const label = t(`map.travelMode.${mode}` as Parameters<typeof t>[0]);
  return (
    <span title={label} aria-label={label} className="w-6 h-6 ml-1 rounded-lg flex items-center justify-center bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
      <Icon as={legMode.Icon} size={12} />
    </span>
  );
}
